import React, { useState, useRef, useCallback, useMemo } from 'react'
import styles from './AnalyticsChart.module.scss'
import type { AnalyticsTimeseriesPoint } from '@/types/analytics'

interface AnalyticsChartProps {
  data: AnalyticsTimeseriesPoint[]
  className?: never
}

const PADDING = { top: 20, right: 20, bottom: 36, left: 52 }
const CHART_HEIGHT = 260

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatNumber(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return n.toString()
}

function computeNiceMax(maxVal: number): number {
  if (maxVal <= 0) return 5
  const magnitude = Math.pow(10, Math.floor(Math.log10(maxVal)))
  const normalized = maxVal / magnitude
  let niceNormalized: number
  if (normalized <= 1) niceNormalized = 1
  else if (normalized <= 2) niceNormalized = 2
  else if (normalized <= 5) niceNormalized = 5
  else niceNormalized = 10
  return Math.max(niceNormalized * magnitude, 5)
}

/** Attempt monotone cubic interpolation (Fritsch-Carlson) for smooth curves */
function buildSmoothPath(
  points: Array<{ x: number; y: number }>
): string {
  if (points.length === 0) return ''
  if (points.length === 1) return `M${points[0].x},${points[0].y}`
  if (points.length === 2) {
    return `M${points[0].x},${points[0].y} L${points[1].x},${points[1].y}`
  }

  const n = points.length
  // Compute slopes
  const dx: number[] = []
  const dy: number[] = []
  const m: number[] = []

  for (let i = 0; i < n - 1; i++) {
    dx.push(points[i + 1].x - points[i].x)
    dy.push(points[i + 1].y - points[i].y)
    m.push(dy[i] / dx[i])
  }

  // Compute tangents using Fritsch-Carlson
  const tangents: number[] = [m[0]]
  for (let i = 1; i < n - 1; i++) {
    if (m[i - 1] * m[i] <= 0) {
      tangents.push(0)
    } else {
      tangents.push((m[i - 1] + m[i]) / 2)
    }
  }
  tangents.push(m[n - 2])

  // Build cubic bezier path
  let path = `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`

  for (let i = 0; i < n - 1; i++) {
    const p0 = points[i]
    const p1 = points[i + 1]
    const segLen = dx[i] / 3

    const cp1x = p0.x + segLen
    const cp1y = p0.y + tangents[i] * segLen
    const cp2x = p1.x - segLen
    const cp2y = p1.y - tangents[i + 1] * segLen

    path += ` C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p1.x.toFixed(1)},${p1.y.toFixed(1)}`
  }

  return path
}

export const AnalyticsChart: React.FC<AnalyticsChartProps> = ({ data }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)
  const [mousePos, setMousePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 })

  const width = 900
  const height = CHART_HEIGHT
  const chartW = width - PADDING.left - PADDING.right
  const chartH = height - PADDING.top - PADDING.bottom

  const maxVal = useMemo(
    () => Math.max(...data.map((d) => Math.max(d.visitors, d.pageviews)), 1),
    [data]
  )
  const niceMax = useMemo(() => computeNiceMax(maxVal), [maxVal])

  const xScale = useCallback(
    (i: number) =>
      PADDING.left + (data.length > 1 ? (i / (data.length - 1)) * chartW : chartW / 2),
    [data.length, chartW]
  )

  const yScale = useCallback(
    (v: number) => PADDING.top + chartH - (v / niceMax) * chartH,
    [chartH, niceMax]
  )

  const visitorsPoints = useMemo(
    () => data.map((d, i) => ({ x: xScale(i), y: yScale(d.visitors) })),
    [data, xScale, yScale]
  )

  const pageviewsPoints = useMemo(
    () => data.map((d, i) => ({ x: xScale(i), y: yScale(d.pageviews) })),
    [data, xScale, yScale]
  )

  const visitorsPath = useMemo(() => buildSmoothPath(visitorsPoints), [visitorsPoints])
  const pageviewsPath = useMemo(() => buildSmoothPath(pageviewsPoints), [pageviewsPoints])

  const baseline = yScale(0)

  const buildAreaPath = (linePath: string, points: Array<{ x: number; y: number }>): string => {
    if (points.length === 0) return ''
    const lastX = points[points.length - 1].x.toFixed(1)
    const firstX = points[0].x.toFixed(1)
    return `${linePath} L${lastX},${baseline.toFixed(1)} L${firstX},${baseline.toFixed(1)} Z`
  }

  const gridLines = useMemo(() => {
    const lines: Array<{ y: number; label: string }> = []
    const steps = 5
    for (let i = 0; i <= steps; i++) {
      const val = (niceMax / steps) * i
      lines.push({ y: yScale(val), label: formatNumber(Math.round(val)) })
    }
    return lines
  }, [niceMax, yScale])

  const xLabels = useMemo(() => {
    const labels: Array<{ x: number; label: string }> = []
    if (data.length === 0) return labels
    const maxLabels = 7
    const step = Math.max(1, Math.floor(data.length / maxLabels))
    for (let i = 0; i < data.length; i += step) {
      labels.push({ x: xScale(i), label: formatDateLabel(data[i].date) })
    }
    const lastIdx = data.length - 1
    if (!labels.find((l) => Math.abs(l.x - xScale(lastIdx)) < 30)) {
      labels.push({ x: xScale(lastIdx), label: formatDateLabel(data[lastIdx].date) })
    }
    return labels
  }, [data, xScale])

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      if (data.length === 0 || !containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const svgScale = width / rect.width
      const scaledX = mouseX * svgScale

      let closest = 0
      let minDist = Infinity
      for (let i = 0; i < data.length; i++) {
        const dist = Math.abs(xScale(i) - scaledX)
        if (dist < minDist) {
          minDist = dist
          closest = i
        }
      }

      setHoverIdx(closest)
      setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
    },
    [data, xScale]
  )

  const handleMouseLeave = useCallback(() => setHoverIdx(null), [])

  const tooltipLeft = useMemo(() => {
    if (hoverIdx === null || !containerRef.current) return 0
    const containerWidth = containerRef.current.clientWidth
    const tooltipWidth = 180
    const x = mousePos.x
    if (x + tooltipWidth + 16 > containerWidth) return x - tooltipWidth - 12
    return x + 12
  }, [hoverIdx, mousePos.x])

  return (
    <div className={styles.container} ref={containerRef}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className={styles.svg}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="visitorsGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--admin-primary)" stopOpacity="0.25" />
            <stop offset="100%" stopColor="var(--admin-primary)" stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="pageviewsGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--admin-info)" stopOpacity="0.15" />
            <stop offset="100%" stopColor="var(--admin-info)" stopOpacity="0.01" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {gridLines.map((g, i) => (
          <g key={i}>
            <line
              x1={PADDING.left}
              y1={g.y}
              x2={width - PADDING.right}
              y2={g.y}
              className={i === 0 ? styles.baseLine : styles.gridLine}
            />
            <text x={PADDING.left - 10} y={g.y + 4} textAnchor="end" className={styles.axisLabel}>
              {g.label}
            </text>
          </g>
        ))}

        {/* X axis labels */}
        {xLabels.map((xl, i) => (
          <text key={i} x={xl.x} y={height - 6} textAnchor="middle" className={styles.axisLabel}>
            {xl.label}
          </text>
        ))}

        {/* Area fills with gradient */}
        <path d={buildAreaPath(pageviewsPath, pageviewsPoints)} fill="url(#pageviewsGradient)" />
        <path d={buildAreaPath(visitorsPath, visitorsPoints)} fill="url(#visitorsGradient)" />

        {/* Lines */}
        <path d={pageviewsPath} className={styles.linePageviews} />
        <path d={visitorsPath} className={styles.lineVisitors} />

        {/* Hover elements */}
        {hoverIdx !== null && data[hoverIdx] && (
          <>
            <line
              x1={xScale(hoverIdx)}
              y1={PADDING.top}
              x2={xScale(hoverIdx)}
              y2={PADDING.top + chartH}
              className={styles.hoverLine}
            />
            <circle
              cx={xScale(hoverIdx)}
              cy={yScale(data[hoverIdx].pageviews)}
              r={4}
              className={styles.dotPageviews}
            />
            <circle
              cx={xScale(hoverIdx)}
              cy={yScale(data[hoverIdx].pageviews)}
              r={8}
              className={styles.dotPageviewsRing}
            />
            <circle
              cx={xScale(hoverIdx)}
              cy={yScale(data[hoverIdx].visitors)}
              r={4}
              className={styles.dotVisitors}
            />
            <circle
              cx={xScale(hoverIdx)}
              cy={yScale(data[hoverIdx].visitors)}
              r={8}
              className={styles.dotVisitorsRing}
            />
          </>
        )}

        {/* Hover capture area */}
        <rect
          x={PADDING.left}
          y={PADDING.top}
          width={chartW}
          height={chartH}
          className={styles.hoverArea}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />
      </svg>

      {/* Tooltip */}
      {hoverIdx !== null && data[hoverIdx] && (
        <div
          className={styles.tooltip}
          style={{
            left: tooltipLeft,
            top: Math.max(mousePos.y - 80, 8),
          }}
        >
          <span className={styles.tooltipDate}>
            {formatDateLabel(data[hoverIdx].date)}
          </span>
          <span className={styles.tooltipRow}>
            <span className={styles.tooltipDotVisitors} />
            <span className={styles.tooltipLabel}>Visitors</span>
            <span className={styles.tooltipValue}>{data[hoverIdx].visitors.toLocaleString()}</span>
          </span>
          <span className={styles.tooltipRow}>
            <span className={styles.tooltipDotPageviews} />
            <span className={styles.tooltipLabel}>Pageviews</span>
            <span className={styles.tooltipValue}>{data[hoverIdx].pageviews.toLocaleString()}</span>
          </span>
        </div>
      )}

      {/* Legend */}
      <div className={styles.legend}>
        <span className={styles.legendItem}>
          <span className={[styles.legendLine, styles.legendVisitors].join(' ')} />
          Visitors
        </span>
        <span className={styles.legendItem}>
          <span className={[styles.legendLine, styles.legendPageviews].join(' ')} />
          Pageviews
        </span>
      </div>
    </div>
  )
}
